import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  useSyncExternalStore,
} from 'react'

// --- Types ---

export type PendingChange = {
  values: Record<string, unknown>
  save: () => Promise<void>
  dirtyFieldCount: number
}

export type SettingsEditContextValue = {
  settingsId: string
  registerChanges: (tab: string, change: PendingChange) => void
  clearChanges: (tab: string) => void
  clearAllChanges: () => void
  hasPendingChanges: boolean
  pendingTabs: Set<string>
  saveAll: () => Promise<void>
  isSaving: boolean
  getPendingValues: (tab: string) => Record<string, unknown> | undefined
  notifyDirtyFieldCount: () => void
}

// --- External store registry ---
// Each settingsId gets its own external store so multiple providers
// (event, guild, user) can coexist without collision.

export type SettingsEditSnapshot = {
  hasPendingChanges: boolean
  isSaving: boolean
  pendingCount: number
  saveAll: () => Promise<void>
}

const DEFAULT_SNAPSHOT: SettingsEditSnapshot = {
  hasPendingChanges: false,
  isSaving: false,
  pendingCount: 0,
  saveAll: async () => {},
}

const externalStores = new Map<
  string,
  {
    snapshot: SettingsEditSnapshot
    listeners: Set<() => void>
  }
>()

const getOrCreateStore = (id: string) => {
  let store = externalStores.get(id)
  if (!store) {
    store = { snapshot: DEFAULT_SNAPSHOT, listeners: new Set() }
    externalStores.set(id, store)
  }
  return store
}

const notifyListeners = (id: string) => {
  const store = externalStores.get(id)
  if (store) store.listeners.forEach((l) => l())
}

export const useSettingsEditExternal = (
  settingsId: string
): SettingsEditSnapshot => {
  return useSyncExternalStore(
    (listener) => {
      const store = getOrCreateStore(settingsId)
      store.listeners.add(listener)
      return () => {
        store.listeners.delete(listener)
      }
    },
    () => getOrCreateStore(settingsId).snapshot
  )
}

// --- Context ---

const SettingsEditContext = createContext<SettingsEditContextValue | null>(null)

export const useSettingsEdit = (): SettingsEditContextValue => {
  const context = useContext(SettingsEditContext)
  if (!context) {
    throw new Error('useSettingsEdit must be used within a SettingsEditProvider')
  }
  return context
}

// --- Provider ---

export const SettingsEditProvider = ({
  settingsId,
  saveOrder,
  children,
}: {
  settingsId: string
  saveOrder: readonly string[]
  children: React.ReactNode
}) => {
  const changesRef = useRef<Map<string, PendingChange>>(new Map())
  const [pendingTabs, setPendingTabs] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)

  const registerChanges = useCallback(
    (tab: string, change: PendingChange) => {
      changesRef.current.set(tab, change)
      setPendingTabs((prev) => {
        if (prev.has(tab)) return prev
        const next = new Set(prev)
        next.add(tab)
        return next
      })
    },
    []
  )

  const clearChanges = useCallback((tab: string) => {
    changesRef.current.delete(tab)
    setPendingTabs((prev) => {
      if (!prev.has(tab)) return prev
      const next = new Set(prev)
      next.delete(tab)
      return next
    })
  }, [])

  const clearAllChanges = useCallback(() => {
    changesRef.current.clear()
    setPendingTabs((prev) => {
      if (prev.size === 0) return prev
      return new Set()
    })
  }, [])

  const saveAll = useCallback(async () => {
    setIsSaving(true)
    try {
      for (const tab of saveOrder) {
        const change = changesRef.current.get(tab)
        if (change) {
          await change.save()
          changesRef.current.delete(tab)
          setPendingTabs((prev) => {
            const next = new Set(prev)
            next.delete(tab)
            return next
          })
        }
      }
    } finally {
      setIsSaving(false)
    }
  }, [saveOrder])

  const getPendingValues = useCallback(
    (tab: string): Record<string, unknown> | undefined => {
      return changesRef.current.get(tab)?.values
    },
    []
  )

  const hasPendingChanges = pendingTabs.size > 0

  // Compute total dirty field count from all pending changes
  const getTotalDirtyFieldCount = useCallback(() => {
    let total = 0
    for (const change of changesRef.current.values()) {
      total += change.dirtyFieldCount || 0
    }
    return total
  }, [])

  // Called by FormChangeTracker when its dirty field count changes.
  // Updates the external store directly without triggering provider re-renders.
  const notifyDirtyFieldCount = useCallback(() => {
    const store = getOrCreateStore(settingsId)
    const totalDirtyFields = getTotalDirtyFieldCount()
    if (store.snapshot.pendingCount !== totalDirtyFields) {
      store.snapshot = { ...store.snapshot, pendingCount: totalDirtyFields }
      notifyListeners(settingsId)
    }
  }, [settingsId, getTotalDirtyFieldCount])

  // Keep external store in sync
  useEffect(() => {
    const store = getOrCreateStore(settingsId)
    const totalDirtyFields = getTotalDirtyFieldCount()
    const next: SettingsEditSnapshot = {
      hasPendingChanges,
      isSaving,
      pendingCount: totalDirtyFields,
      saveAll,
    }

    if (
      store.snapshot.hasPendingChanges !== next.hasPendingChanges ||
      store.snapshot.isSaving !== next.isSaving ||
      store.snapshot.pendingCount !== next.pendingCount ||
      store.snapshot.saveAll !== next.saveAll
    ) {
      store.snapshot = next
      notifyListeners(settingsId)
    }
  }, [settingsId, hasPendingChanges, isSaving, pendingTabs.size, saveAll, getTotalDirtyFieldCount])

  // Reset external store on unmount
  useEffect(() => {
    return () => {
      const store = externalStores.get(settingsId)
      if (store) {
        store.snapshot = DEFAULT_SNAPSHOT
        notifyListeners(settingsId)
      }
    }
  }, [settingsId])

  const contextValue = useMemo<SettingsEditContextValue>(
    () => ({
      settingsId,
      registerChanges,
      clearChanges,
      clearAllChanges,
      hasPendingChanges,
      pendingTabs,
      saveAll,
      isSaving,
      getPendingValues,
      notifyDirtyFieldCount,
    }),
    [
      settingsId,
      registerChanges,
      clearChanges,
      clearAllChanges,
      hasPendingChanges,
      pendingTabs,
      saveAll,
      isSaving,
      getPendingValues,
      notifyDirtyFieldCount,
    ]
  )

  return (
    <SettingsEditContext.Provider value={contextValue}>
      {children}
    </SettingsEditContext.Provider>
  )
}
